import { useEffect, useState, useCallback } from "react";
import { useTelegram } from "./useTelegram";

export type TelegramCommand =
  | "about"
  | "sub"
  | "vip"
  | "help"
  | "profile"
  | "admin"
  | "register"
  | "wishlist"
  | "contact";

export const useTelegramCommands = () => {
  const { isInTelegram, initData, hapticFeedback } = useTelegram();
  const [activeCommand, setActiveCommand] = useState<TelegramCommand | null>(null);
  const [commandParams, setCommandParams] = useState<Record<string, string>>({});

  // Parse start param from URL or initData
  useEffect(() => {
    if (!isInTelegram) return;

    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const startParam = urlParams.get("startapp") || urlParams.get("start");
    
    // Parse command from start param (e.g., "about_123" -> { command: "about", params: { id: "123" } })
    if (startParam) {
      parseCommand(startParam);
    } else if (initData) {
      // Parse from initData if available
      const initDataObj = new URLSearchParams(initData);
      const startFromInit = initDataObj.get("start_param");
      if (startFromInit) {
        parseCommand(startFromInit);
      }
    }
  }, [isInTelegram, initData]);

  const parseCommand = (param: string) => {
    const parts = param.split("_");
    const command = parts[0] as TelegramCommand;
    
    if (["about", "sub", "vip", "help", "profile", "admin", "register", "wishlist", "contact"].includes(command)) {
      setActiveCommand(command);
      
      // Parse additional params
      const params: Record<string, string> = {};
      if (parts.length > 1) {
        params.id = parts[1];
        if (parts[2]) params.extra = parts[2];
      }
      setCommandParams(params);
      
      // Haptic feedback on command detection
      hapticFeedback.notification("success");
    }
  };

  // Execute a command programmatically
  const executeCommand = useCallback((command: TelegramCommand, params?: Record<string, string>) => {
    setActiveCommand(command);
    setCommandParams(params || {});
    hapticFeedback.impact("light");
    
    const commandActions: Record<TelegramCommand, string> = {
      about: "/about",
      sub: "/subscribe",
      vip: "/vip",
      help: "/help",
      profile: "/profile",
      admin: "/admin",
      register: "/register",
      wishlist: "/wishlist",
      contact: "/contact",
    };
    
    return commandActions[command];
  }, [hapticFeedback]);

  // Share command with friends
  const shareCommand = useCallback((command: TelegramCommand, params?: Record<string, string>) => {
    const botUsername = (import.meta as any).env?.VITE_BOT_USERNAME || "";
    const paramString = params ? `_${Object.values(params).join("_")}` : "";
    const shareUrl = `https://t.me/${botUsername}?startapp=${command}${paramString}`;
    
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink(shareUrl);
    } else {
      window.open(shareUrl, "_blank");
    }
  }, []);

  // Open external link in Telegram
  const openExternalLink = useCallback((url: string) => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(url);
    } else {
      window.open(url, "_blank");
    }
  }, []);

  // Show popup in Telegram
  const showPopup = useCallback((params: {
    title?: string;
    message: string;
    buttons?: Array<{ id?: string; type?: "default" | "ok" | "close" | "cancel" | "destructive"; text: string }>;
  }) => {
    return new Promise<string | null>((resolve) => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup(params, (buttonId) => {
          resolve(buttonId || null);
        });
      } else {
        alert(params.message);
        resolve(null);
      }
    });
  }, []);

  // Show confirm dialog
  const showConfirm = useCallback((message: string) => {
    return new Promise<boolean>((resolve) => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showConfirm(message, (confirmed) => {
          resolve(confirmed);
        });
      } else {
        resolve(confirm(message));
      }
    });
  }, []);

  // Set clipboard text
  const setClipboard = useCallback((text: string) => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.readTextFromClipboard((clipboardText) => {
        navigator.clipboard.writeText(text);
      });
    } else {
      navigator.clipboard.writeText(text);
    }
  }, []);

  return {
    activeCommand,
    commandParams,
    isInTelegram,
    executeCommand,
    shareCommand,
    openExternalLink,
    showPopup,
    showConfirm,
    setClipboard,
    parseCommand,
  };
};
